/**
 * Session Store — Winnery Gateway Integration
 * 
 * Manages:
 * - session_id from URL query params (iframe integration)
 * - Player session data from API
 * - Currencies & calculator presets from API
 * - Balance from API (human-readable string)
 * - Demo mode when no session_id is provided
 * 
 * @see INTEGRATION.md for backend connection guide
 */

import { create } from 'zustand';
import type { WinneryCurrency } from '@/types/winnery';
import { GAME_CONFIG } from '@/types';
import {
  getSessionIdFromUrl,
  isApiConfigured,
} from '@/services/winneryApi';

// ==========================================
// Store Interface
// ==========================================

interface SessionStore {
  // === Connection State ===
  sessionId: string | null;
  isConnected: boolean;
  isLoading: boolean;
  error: string | null;
  isDemoMode: boolean;
  
  // === Player Data ===
  playerId: string | null;
  language: string;
  
  // === Currency ===
  currencyCode: string;
  currencyDecimals: number;
  currencies: WinneryCurrency[];
  
  // === Balance (from API) ===
  balanceHuman: string;        // Formatted string from API for UI display
  balanceRaw: number;          // Raw value in smallest units (cents)
  
  // === Calculator Presets ===
  presets: number[];           // Bet presets for current currency
  minBet: number;              // Minimum bet for current currency, in smallest units
  maxBet: number;              // Maximum bet for current currency, in smallest units
  defaultBet: number;          // Default bet for current currency, in smallest units
  
  // === Actions ===
  initialize: () => Promise<void>;        // Init on app start
  refreshBalance: () => Promise<void>;    // Refresh balance from API
  setDemoBalance: (amount: number) => void; // Set balance in demo mode
  reset: () => void;
}

// ==========================================
// Demo-mode calculator defaults
// ==========================================

const DEFAULT_PRESETS = [50, 100, 250];
const DEFAULT_MIN_BET = 10;
const DEFAULT_BET = 100;
const DEMO_CURRENCY_CODE = 'USD';
const DEFAULT_CURRENCY_DECIMALS = 2;
const DEMO_BALANCE_MINOR = 500000;

// ==========================================
// Store Implementation
// ==========================================

export const useSessionStore = create<SessionStore>((set, get) => ({
  // === Initial State ===
  sessionId: null,
  isConnected: false,
  isLoading: false,
  error: null,
  isDemoMode: false,
  
  playerId: null,
  language: 'en',
  
  currencyCode: DEMO_CURRENCY_CODE,
  currencyDecimals: DEFAULT_CURRENCY_DECIMALS,
  currencies: [],
  
  balanceHuman: '',
  balanceRaw: 0,
  
  presets: DEFAULT_PRESETS,
  minBet: DEFAULT_MIN_BET,
  maxBet: GAME_CONFIG.MAX_BET,
  defaultBet: DEFAULT_BET,
  
  // === Actions ===
  
  /**
   * Initialize session on app start.
   * 1. Extract session_id from URL
   * 2. If no session_id or no API configured → demo mode
   * 3. If session_id exists → fetch session, currencies, balance from API
   */
  initialize: async () => {
    set({ isLoading: true, error: null });

    let sessionId = getSessionIdFromUrl();

    // Dev convenience: if API is configured but no session_id in URL,
    // auto-generate a stable session_id (per-tab) and use server mode.
    if (!sessionId && isApiConfigured()) {
      const stored = sessionStorage.getItem('rolltower_dev_session_id');
      if (stored) {
        sessionId = stored;
      } else {
        sessionId = `dev-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36)}`;
        sessionStorage.setItem('rolltower_dev_session_id', sessionId);
      }
    }

    // No session_id or no API → demo mode
    if (!sessionId || !isApiConfigured()) {
      set({
        sessionId: null,
        isDemoMode: true,
        isConnected: true,
        isLoading: false,
        balanceHuman: (DEMO_BALANCE_MINOR / 100).toFixed(DEFAULT_CURRENCY_DECIMALS),
        balanceRaw: DEMO_BALANCE_MINOR,
        currencyCode: DEMO_CURRENCY_CODE,
        currencyDecimals: DEFAULT_CURRENCY_DECIMALS,
        presets: DEFAULT_PRESETS,
        minBet: DEFAULT_MIN_BET,
        maxBet: GAME_CONFIG.MAX_BET,
        defaultBet: DEFAULT_BET,
      });
      return;
    }

    // Server mode: just set sessionId. The WS layer will send `config` and
    // `balance_update` messages on connect — those overwrite currency, presets
    // and balance. No REST calls — gateway is the single source of truth.
    set({
      sessionId,
      isConnected: true,
      isLoading: false,
      isDemoMode: false,
      currencyCode: '',
      currencyDecimals: DEFAULT_CURRENCY_DECIMALS,
      presets: DEFAULT_PRESETS,
      minBet: DEFAULT_MIN_BET,
      maxBet: GAME_CONFIG.MAX_BET,
      defaultBet: DEFAULT_BET,
    });
  },
  
  /**
   * No-op in server mode: balance is pushed via WS `balance_update` events.
   * Kept for demo-mode compatibility (gameStore.syncBalance still calls it).
   */
  refreshBalance: async () => {
    // Balance flows from gateway → WS balance_update → gameStore.setBalance.
    // No REST call needed.
  },
  
  /**
   * Set balance in demo mode (used by gameStore sync).
   */
  setDemoBalance: (amountMinor: number) => {
    const { isDemoMode, currencyDecimals } = get();
    if (!isDemoMode) return;
    
    const minorPerMajor = Math.pow(10, currencyDecimals);
    set({
      balanceHuman: (amountMinor / minorPerMajor).toFixed(currencyDecimals),
      balanceRaw: amountMinor,
    });
  },
  
  /**
   * Reset store to initial state.
   */
  reset: () => {
    set({
      sessionId: null,
      isConnected: false,
      isLoading: false,
      error: null,
      isDemoMode: false,
      playerId: null,
      language: 'en',
      currencyCode: DEMO_CURRENCY_CODE,
      currencyDecimals: DEFAULT_CURRENCY_DECIMALS,
      currencies: [],
      balanceHuman: '',
      balanceRaw: 0,
      presets: DEFAULT_PRESETS,
      minBet: DEFAULT_MIN_BET,
      maxBet: GAME_CONFIG.MAX_BET,
      defaultBet: DEFAULT_BET,
    });
  },
}));

// ==========================================
// Selectors
// ==========================================

export const selectIsDemo = (state: SessionStore) => state.isDemoMode;
export const selectPresets = (state: SessionStore) => state.presets;
export const selectIsReady = (state: SessionStore) => state.isConnected && !state.isLoading;

export default useSessionStore;







